import SendWidget from './send-widget';
import ReceiveWidget from './receive-widget';
import type TokenMetadataItem from '../tokens/types/token-metadata-item.ts';
import IconButton from '../common/icon-button';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import {useCallback, useEffect, useRef, useState} from 'react';
import styles from './styles.module.css'
import {styledMod} from '@/utils/styled-mod.tsx';
import setDataToLocaleStorage from '@/utils/locale-storage/set-data-to-locale-storage.ts';
import LocaleStorageKey from '@/enums/locale-storage-key.ts';
import getDataFromLocaleStorage from '@/utils/locale-storage/get-data-from-locale-storage.ts';

type SwapWidgetProps = {
  tokensMetadata: TokenMetadataItem[];
  onSendTokenAddressChange: (tokenName: string, tokenAddress: string, imgSrc: string) => void;
  onReceiveTokenAddressChange: (tokenName: string, tokenAddress: string, imgSrc: string) => void;
  onSendAmountValueChange: (valueAmount: string) => void;
  onReceiveAmountValueChange: (valueAmount: string) => void;
  onSwapParametersChange: () => void;
  isVisibleSlippageSettingDialog: boolean;
  onVisibleSlippageSettingDialogChange: (isVisible: boolean) => void;
};


export const WidgetsContainer = styledMod('div', styles['widgets-container']);
export const WidgetDropdownContainer = styledMod('div', styles['widget-dropdown-container']);

const SwapWidget: React.FunctionComponent<SwapWidgetProps> = (props: SwapWidgetProps) => {
  const [rectParameters, setRectParameters] = useState<DOMRect | null>(null);
  const [tokensMetadata, setTokensMetadata] = useState<TokenMetadataItem[]>([]);

  const swapSelectTokenContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (swapSelectTokenContainerRef?.current) {
      const rect: DOMRect = swapSelectTokenContainerRef.current.getBoundingClientRect();

      setRectParameters({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
      } as DOMRect);

    }
  }, [swapSelectTokenContainerRef]);


  useEffect(() => {

    const tokensMetadataFavoritesString: string = getDataFromLocaleStorage(LocaleStorageKey.FavoritesTokens) || '';
    const tokensMetadataFavoritesAddresses: string[] = tokensMetadataFavoritesString > '' ? tokensMetadataFavoritesString.split(';') : [];
    if (tokensMetadataFavoritesAddresses.length > 0) {
      setTokensMetadata(props.tokensMetadata.map((element: TokenMetadataItem) => {
        if (tokensMetadataFavoritesAddresses.includes(element.address)) {
          return {
            ...element,
            isFavorite: true,
          };
        }
        return element;
      }));
    } else {
      setTokensMetadata(props.tokensMetadata);
    }
  }, [props.tokensMetadata]);

  const onTokenAddressFavoritesChange = useCallback((elementSymbol: string) => {
    const tokensMetadataModified: TokenMetadataItem[] = tokensMetadata.map((tokenElement: TokenMetadataItem) => ({
      ...tokenElement,
      isFavorite: tokenElement.symbol === elementSymbol ? !tokenElement.isFavorite : tokenElement.isFavorite,
    }))
    setTokensMetadata(tokensMetadataModified);
    const tokensMetadataFavoritesAddresses: string[] = tokensMetadataModified
      .filter((element: TokenMetadataItem) => element.isFavorite)
      .map((element: TokenMetadataItem) => element.address)

    setDataToLocaleStorage(LocaleStorageKey.FavoritesTokens, tokensMetadataFavoritesAddresses.join(';'));
  }, [tokensMetadata]);

  return (
    <WidgetsContainer ref={swapSelectTokenContainerRef} id="swap-select-token-container">
      <SendWidget
        tokensMetadata={tokensMetadata}
        onAmountValueChange={props.onSendAmountValueChange}
        onTokenAddressChange={props.onSendTokenAddressChange}
        onVisibleSlippageSettingDialogChange={props.onVisibleSlippageSettingDialogChange}
        isVisibleSlippageSettingDialog={props.isVisibleSlippageSettingDialog}
        rectParameters={rectParameters}
        onTokenAddressFavoritesChange={onTokenAddressFavoritesChange}
      />
      <IconButton variant="outlined" onClick={props.onSwapParametersChange}>
        <ChangeCircleIcon fontSize="large"/>
      </IconButton>
      <ReceiveWidget
        tokensMetadata={tokensMetadata}
        onAmountValueChange={props.onReceiveAmountValueChange}
        onTokenAddressChange={props.onReceiveTokenAddressChange}
        rectParameters={rectParameters}
        onTokenAddressFavoritesChange={onTokenAddressFavoritesChange}
      />
    </WidgetsContainer>
  );
};

export default SwapWidget;
